import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Download, UserPlus } from 'lucide-react';
import { businessPublicService, businessMappingService, businessPlantService } from '../../../services/businessApi';
import { useAuth } from '../../../context/AuthContext';
import StepCell from './StepCell';
import StepEditorModal from './StepEditorModal';
import AddSupplierModal from './AddSupplierModal';
import '../../../styles/businessTheme.css';
import './Checklist.css';

export default function PlantChecklist() {
  const { isBusinessLead } = useAuth();
  const [plants, setPlants] = useState([]);
  const [plantId, setPlantId] = useState('');
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { mapping, entry }
  const [exporting, setExporting] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);

  useEffect(() => {
    businessPublicService.listPlants().then(res => {
      setPlants(res.data.plants);
      if (res.data.plants.length > 0) setPlantId(res.data.plants[0]._id);
    });
  }, []);

  const load = useCallback(async () => {
    if (!plantId) return;
    setLoading(true);
    const res = await businessMappingService.list({ plantId });
    setMappings(res.data.mappings);
    setLoading(false);
  }, [plantId]);

  useEffect(() => { load(); }, [load]);

  const stepColumns = mappings[0]?.steps.map(s => ({ id: s.step._id, name: s.step.name })) || [];

  const handleExport = async () => {
    if (!plantId) return;
    setExporting(true);
    try {
      const res = await businessPlantService.exportTracker(plantId);
      const plant = plants.find(p => p._id === plantId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${plant?.code || 'tracker'}-supplier-tracker.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="checklist-page biz-theme biz-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Onboarding Checklist</h1>
          <p className="page-subtitle">{mappings.length} supplier{mappings.length !== 1 ? 's' : ''} mapped to this plant</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isBusinessLead && (
            <button className="btn btn-primary" onClick={() => setShowAddSupplier(true)} disabled={!plantId}>
              <UserPlus size={14} /> Add Supplier
            </button>
          )}
          <button className="btn btn-ghost" onClick={handleExport} disabled={!plantId || exporting}>
            <Download size={14} /> {exporting ? 'Exporting...' : 'Export Tracker'}
          </button>
        </div>
      </div>

      <div className="checklist-toolbar">
        <select value={plantId} onChange={e => setPlantId(e.target.value)}>
          {plants.map(p => <option key={p._id} value={p._id}>{p.name} ({p.code})</option>)}
        </select>
      </div>

      <div className="card checklist-table-wrap checklist-table">
        <table>
          <thead>
            <tr>
              <th>Supplier</th>
              {stepColumns.map(col => <th key={col.id}>{col.name}</th>)}
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map(m => (
              <tr key={m._id}>
                <td className="checklist-supplier-cell">
                  <div className="checklist-supplier-name">
                    <Link to={`/business/suppliers/${m.supplier._id}`}>{m.supplier.name}</Link>
                  </div>
                  <div className="checklist-supplier-code">{m.supplier.vendorCode}{m.newCode && m.newCode !== m.supplier.vendorCode ? ` (new: ${m.newCode})` : ''}</div>
                </td>
                {m.steps.map(entry => (
                  <StepCell
                    key={entry._id}
                    entry={entry}
                    editable={isBusinessLead}
                    onClick={() => setEditing({ mapping: m, entry })}
                  />
                ))}
                <td className="checklist-notes-cell">{m.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && mappings.length === 0 && <div className="empty-state">No suppliers mapped to this plant yet</div>}
      </div>

      {editing && (
        <StepEditorModal
          mappingId={editing.mapping._id}
          entry={editing.entry}
          stepName={editing.entry.step.name}
          supplierName={editing.mapping.supplier.name}
          plantName={editing.mapping.plant.name}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {showAddSupplier && (
        <AddSupplierModal
          plantId={plantId}
          plantName={plants.find(p => p._id === plantId)?.name}
          onClose={() => setShowAddSupplier(false)}
          onSaved={() => { setShowAddSupplier(false); load(); }}
        />
      )}
    </div>
  );
}
